// Translates raw keyboard events into the value a question should accept.
// Used by usePageKeyboard wiring in PagedSurvey + ConversationalSurvey so
// pressing "7" on an NPS question writes 7, "B" on a yes-no writes false, etc.

import type { SurveyQuestion } from '../types.js';

const SCALE_TYPES = new Set(['nps', 'csat', 'ces', 'likert', 'scale', 'emojiScale']);
const LETTER_CHOICE_TYPES = new Set([
  'singleChoice',
  'choice',
  'yesNo',
  'dropdown',
  'imageChoice',
]);

const SCALE_DEFAULTS: Record<string, { min: number; max: number }> = {
  nps: { min: 0, max: 10 },
  csat: { min: 1, max: 5 },
  ces: { min: 1, max: 7 },
  likert: { min: 1, max: 5 },
  emojiScale: { min: 1, max: 5 },
  // `scale` is fully customer-configured; fall back to sensible defaults
  // if settings are missing.
  scale: { min: 1, max: 5 },
};

export function valueForNumberKey(question: SurveyQuestion | null | undefined, key: number): unknown {
  if (!question) return undefined;
  if (!SCALE_TYPES.has(question.type)) return undefined;
  const range = SCALE_DEFAULTS[question.type] ?? { min: 1, max: 5 };
  const min = question.settings?.min ?? range.min;
  const max = question.settings?.max ?? range.max;
  if (key < min || key > max) return undefined;
  return key;
}

export function valueForLetterKey(question: SurveyQuestion | null | undefined, index: number): unknown {
  if (!question) return undefined;
  if (!LETTER_CHOICE_TYPES.has(question.type)) return undefined;
  if (question.type === 'yesNo') {
    return [true, false][index];
  }
  const opt = question.settings?.options?.[index];
  return opt?.value;
}

// Surfaces use this to decide whether to wire number/letter shortcuts —
// when there are multiple visible questions on a page, `1` is ambiguous and
// shortcuts are disabled to prevent surprise selections.
export function supportsKeyboardShortcuts(question: SurveyQuestion | null | undefined): boolean {
  if (!question) return false;
  return SCALE_TYPES.has(question.type) || LETTER_CHOICE_TYPES.has(question.type);
}
